/// <reference path="./svg.d.ts" />
import { PageGen, PdfRect, PdfPoint, PdfPen, PdfBrush, PdfColor } from '../pdf';
import '../draw/primitives';

export interface SvgNode {
  tag: string;
  attrs: Record<string, string>;
  children: SvgNode[];
  text?: string;
}

export interface SvgDrawOptions {
  keepAspectRatio?: boolean; // default true
  fillFully?: boolean; // default false
  beforeDraw?: (node: SvgNode) => boolean | void;
}

/**
 * 2D Matrix for transformations
 */
class Matrix2D {
  constructor(
    public a = 1,
    public b = 0,
    public c = 0,
    public d = 1,
    public e = 0,
    public f = 0
  ) {}

  multiply(other: Matrix2D): Matrix2D {
    return new Matrix2D(
      this.a * other.a + this.c * other.b,
      this.b * other.a + this.d * other.b,
      this.a * other.c + this.c * other.d,
      this.b * other.c + this.d * other.d,
      this.a * other.e + this.c * other.f + this.e,
      this.b * other.e + this.d * other.f + this.f
    );
  }

  transformPoint(x: number, y: number): { x: number; y: number } {
    return {
      x: this.a * x + this.c * y + this.e,
      y: this.b * x + this.d * y + this.f,
    };
  }

  static parse(str: string): Matrix2D {
    let result = new Matrix2D();
    const regex = /([a-z]+)\s*\(([^)]+)\)/gi;
    let match;
    while ((match = regex.exec(str)) !== null) {
      const type = match[1].toLowerCase();
      const p = match[2]
        .split(/[\s,]+/)
        .filter((s) => s.trim())
        .map(parseFloat);
      let m = new Matrix2D();
      if (type === 'matrix' && p.length === 6) m = new Matrix2D(p[0], p[1], p[2], p[3], p[4], p[5]);
      else if (type === 'translate') m = new Matrix2D(1, 0, 0, 1, p[0], p[1] || 0);
      else if (type === 'scale') m = new Matrix2D(p[0], 0, 0, p[1] || p[0], 0, 0);
      else if (type === 'rotate') {
        const a = (p[0] * Math.PI) / 180;
        m = new Matrix2D(Math.cos(a), Math.sin(a), -Math.sin(a), Math.cos(a), 0, 0);
        if (p.length === 3) {
          const t1 = new Matrix2D(1, 0, 0, 1, p[1], p[2]);
          const t2 = new Matrix2D(1, 0, 0, 1, -p[1], -p[2]);
          m = t1.multiply(m).multiply(t2);
        }
      }
      result = result.multiply(m);
    }
    return result;
  }
}

/**
 * Robust, lightweight XML parser for SVG
 */
export function parseXml(xml: string): SvgNode | null {
  const root: SvgNode = { tag: 'root', attrs: {}, children: [] };
  const stack: SvgNode[] = [root];

  const tagRegex = /<!\[CDATA\[([\s\S]*?)\]\]>|<(\/?)([a-zA-Z0-9\-:_]+)([^>]*?)(\/?)>|([^<]+)/g;
  const attrRegex = /([a-zA-Z0-9\-:_]+)=(?:'([^']*)'|"([^"]*)")/g;

  let match;
  while ((match = tagRegex.exec(xml)) !== null) {
    const [, cdata, isClosing, tagName, attrsStr, isSelfClosing, text] = match;
    if (cdata) {
      stack[stack.length - 1].text = (stack[stack.length - 1].text || '') + cdata;
      continue;
    }
    if (text) {
      stack[stack.length - 1].text = (stack[stack.length - 1].text || '') + text;
      continue;
    }
    if (!tagName) continue;

    if (isClosing) {
      if (stack.length > 1) stack.pop();
    } else {
      const node: SvgNode = { tag: tagName, attrs: {}, children: [] };
      if (attrsStr) {
        let attrMatch;
        while ((attrMatch = attrRegex.exec(attrsStr)) !== null) {
          node.attrs[attrMatch[1]] = attrMatch[2] || attrMatch[3] || '';
        }
      }
      stack[stack.length - 1].children.push(node);
      if (!isSelfClosing && !isSelfClosingTag(tagName)) stack.push(node);
    }
  }
  return root.children.length > 0 ? root.children[0] : null;
}

function isSelfClosingTag(tag: string): boolean {
  return [
    'path',
    'rect',
    'circle',
    'ellipse',
    'line',
    'polyline',
    'polygon',
    'image',
    'use',
    'stop',
    'meta',
    'metadata',
  ].includes(tag.toLowerCase());
}

PageGen.prototype.drawSvg = function (
  svgContent: string,
  rect: PdfRect,
  options?: SvgDrawOptions
): void {
  const root = parseXml(svgContent);
  if (root?.tag.toLowerCase() !== 'svg') return;

  let vb = { x: 0, y: 0, w: 100, h: 100 };
  if (root.attrs.viewBox) {
    const parts = root.attrs.viewBox
      .split(/[\s,]+/)
      .filter((s) => s.trim())
      .map(parseFloat);
    if (parts.length === 4) vb = { x: parts[0], y: parts[1], w: parts[2], h: parts[3] };
  } else {
    if (root.attrs.width) vb.w = parseFloat(root.attrs.width);
    if (root.attrs.height) vb.h = parseFloat(root.attrs.height);
  }

  const styleMap: Record<string, Record<string, string>> = {};
  const gradients: Record<string, PdfColor> = {};
  const gradientNodes: Record<string, SvgNode> = {};

  function processDefs(node: SvgNode) {
    const tag = node.tag.toLowerCase();
    if (tag === 'style') {
      const css = (node.text || '').replace(/\/\*[\s\S]*?\*\//g, '');
      const rules = css.match(/\.([^{]+)\{([^}]+)\}/g);
      if (rules)
        rules.forEach((rule) => {
          const m = /\.([^{]+)\{([^}]+)\}/.exec(rule);
          if (m) {
            const className = m[1].trim();
            styleMap[className] = m[2].split(';').reduce(
              (acc, d) => {
                const [k, v] = d.split(':');
                if (k && v) acc[k.trim()] = v.trim();
                return acc;
              },
              {} as Record<string, string>
            );
          }
        });
    }
    if (tag === 'lineargradient' && node.attrs.id) {
      gradientNodes[node.attrs.id] = node;
    }
    node.children.forEach(processDefs);
  }
  processDefs(root);

  for (const id in gradientNodes) {
    let current: SvgNode | undefined = gradientNodes[id];
    let stops: SvgNode[] = [];
    const seen = new Set<string>();
    while (current && !seen.has(current.attrs.id)) {
      seen.add(current.attrs.id);
      stops = current.children.filter((c) => c.tag.toLowerCase() === 'stop');
      if (stops.length > 0) break;
      const href: string | undefined = current.attrs['xlink:href'] || current.attrs.href;
      current = href?.startsWith('#') ? gradientNodes[href.substring(1)] : undefined;
    }
    if (stops.length > 0) {
      const stop = stops[Math.floor(stops.length / 2)];
      const colorStr =
        stop.attrs['stop-color'] || (/stop-color:\s*([^;]+)/.exec(stop.attrs.style))?.[1];
      const opacity = parseFloat(
        stop.attrs['stop-opacity'] ||
          (/stop-opacity:\s*([^;]+)/.exec(stop.attrs.style))?.[1] ||
          '1'
      );
      if (colorStr) gradients[id] = parseColor(colorStr, opacity, gradients);
    }
  }

  const scaleX = rect.width / vb.w;
  const scaleY = rect.height / vb.h;
  let finalScaleX = scaleX,
    finalScaleY = scaleY;
  if (options?.keepAspectRatio !== false) {
    const scale = options?.fillFully ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);
    finalScaleX = finalScaleY = scale;
  }
  const offsetX = rect.x - vb.x * finalScaleX + (rect.width - vb.w * finalScaleX) / 2;
  const offsetY = rect.y + (rect.height - vb.h * finalScaleY) / 2 - vb.y * finalScaleY;

  const self = this;
  function drawNode(
    node: SvgNode,
    parentPen: PdfPen | null,
    parentBrush: PdfBrush | null,
    parentMatrix: Matrix2D,
    parentFillRule?: 'nonzero' | 'evenodd'
  ): void {
    if (options?.beforeDraw?.(node) === false) return;
    let matrix = parentMatrix;
    if (node.attrs.transform) matrix = matrix.multiply(Matrix2D.parse(node.attrs.transform));
    const {
      pen,
      brush,
      fillRule: nodeFillRule,
    } = parseStyle(node, parentPen, parentBrush, styleMap, gradients, finalScaleX);
    const fillRule = nodeFillRule ?? parentFillRule;
    const tag = node.tag.toLowerCase();

    // Map SVG Y to layout-space Y (both top-left origin, Y increases downward)
    const mapY = (svgY: number) => svgY * finalScaleY + offsetY;

    switch (tag) {
      case 'g':
      case 'svg':
        node.children.forEach((child) => drawNode(child, pen, brush, matrix, fillRule));
        break;
      case 'rect': {
        const x = parseFloat(node.attrs.x || '0'),
          y = parseFloat(node.attrs.y || '0');
        const w = parseFloat(node.attrs.width || '0'),
          h = parseFloat(node.attrs.height || '0');
        const p1 = matrix.transformPoint(x, y),
          p2 = matrix.transformPoint(x + w, y);
        const p3 = matrix.transformPoint(x + w, y + h),
          p4 = matrix.transformPoint(x, y + h);
        self.drawPolygon(pen, brush, [
          new PdfPoint(p1.x * finalScaleX + offsetX, mapY(p1.y)),
          new PdfPoint(p2.x * finalScaleX + offsetX, mapY(p2.y)),
          new PdfPoint(p3.x * finalScaleX + offsetX, mapY(p3.y)),
          new PdfPoint(p4.x * finalScaleX + offsetX, mapY(p4.y)),
        ]);
        break;
      }
      case 'circle':
      case 'ellipse': {
        const cx = parseFloat(node.attrs.cx || '0'),
          cy = parseFloat(node.attrs.cy || '0');
        const rx =
          tag === 'circle'
            ? parseFloat(node.attrs.r || '0')
            : parseFloat(node.attrs.rx || '0');
        const ry =
          tag === 'circle'
            ? parseFloat(node.attrs.r || '0')
            : parseFloat(node.attrs.ry || '0');
        const center = matrix.transformPoint(cx, cy);
        const scaledRx = rx * matrix.a * finalScaleX,
          scaledRy = ry * matrix.d * finalScaleY;
        self.drawEllipse(
          pen,
          brush,
          new PdfRect(
            center.x * finalScaleX + offsetX - scaledRx,
            mapY(center.y) - scaledRy,
            scaledRx * 2,
            scaledRy * 2
          )
        );
        break;
      }
      case 'polygon':
      case 'polyline': {
        const pointsStr = node.attrs.points;
        if (pointsStr) {
          const nums = pointsStr
            .trim()
            .split(/[\s,]+/)
            .filter((s) => s.trim())
            .map(parseFloat);
          const pts: PdfPoint[] = [];
          for (let j = 0; j + 1 < nums.length; j += 2) {
            const p = matrix.transformPoint(nums[j], nums[j + 1]);
            pts.push(new PdfPoint(p.x * finalScaleX + offsetX, mapY(p.y)));
          }
          if (pts.length >= 2) self.drawPolygon(pen, tag === 'polyline' ? null : brush, pts);
        }
        break;
      }
      case 'path': {
        const {d} = node.attrs;
        if (d) {
          const transformedD = transformPathData(
            d,
            matrix,
            finalScaleX,
            finalScaleY,
            offsetX,
            offsetY
          );
          self.page.moveTo(0, self.page.getHeight());
          self.page.drawSvgPath(transformedD, {
            color: brush ? brush.color.toPdfLibColor() : undefined,
            borderColor: pen ? pen.color.toPdfLibColor() : undefined,
            borderWidth: pen ? pen.thickness : undefined,
            borderDashArray: pen?.dashArray ?? undefined,
            opacity: brush ? brush.color.opacity : undefined,
            borderOpacity: pen ? pen.color.opacity : undefined,
            rule: fillRule,
          } as any);
          self.page.moveTo(0, 0);
        }
        break;
      }
    }
  }
  const rootFillRuleStr = (/fill-rule:\s*([^;]+)/.exec(root.attrs.style))?.[1]?.trim();
  const rootFillRule =
    rootFillRuleStr === 'evenodd' || rootFillRuleStr === 'nonzero' ? rootFillRuleStr : undefined;
  drawNode(root, null, null, new Matrix2D(), rootFillRule);
};

function parseStyle(
  node: SvgNode,
  parentPen: PdfPen | null,
  parentBrush: PdfBrush | null,
  styleMap: Record<string, Record<string, string>>,
  gradients: Record<string, PdfColor>,
  scale: number
) {
  const attrs = { ...node.attrs };
  if (attrs.class)
    attrs.class.split(/\s+/).forEach((cn) => Object.assign(attrs, styleMap[cn] || {}));
  const fill = attrs.fill || (/fill:\s*([^;]+)/.exec(attrs.style))?.[1];
  const stroke = attrs.stroke || (/stroke:\s*([^;]+)/.exec(attrs.style))?.[1];
  const strokeWidth = attrs['stroke-width'] || (/stroke-width:\s*([^;]+)/.exec(attrs.style))?.[1];
  const dashArrayStr =
    attrs['stroke-dasharray'] || (/stroke-dasharray:\s*([^;]+)/.exec(attrs.style))?.[1];
  const opacity = parseFloat(attrs.opacity || attrs['fill-opacity'] || '1');
  const fillRule = (
    attrs['fill-rule'] || (/fill-rule:\s*([^;]+)/.exec(attrs.style))?.[1]
  )?.trim() as 'nonzero' | 'evenodd' | undefined;
  const brush =
    fill === 'none'
      ? null
      : fill
        ? new PdfBrush(parseColor(fill, opacity, gradients))
        : parentBrush;
  const pen =
    stroke === 'none'
      ? null
      : stroke || strokeWidth
        ? new PdfPen(
            stroke
              ? parseColor(stroke, opacity, gradients)
              : parentPen?.color || PdfColor.fromRgb(0, 0, 0),
            (strokeWidth ? parseFloat(strokeWidth) : parentPen?.thickness || 1) * scale
          )
        : parentPen;
  if (pen && dashArrayStr)
    pen.dashArray = dashArrayStr.split(/[\s,]+/).map((v) => parseFloat(v) * scale);
  return { pen, brush, fillRule };
}

function parseColor(color: string, opacity: number, gradients: Record<string, PdfColor>): PdfColor {
  if (color.startsWith('#')) return PdfColor.fromHex(color, opacity);
  if (color.startsWith('url(#')) {
    const id = (/url\(#([^)]+)\)/.exec(color))?.[1];
    if (id && gradients[id]) return gradients[id];
  }
  const colors: Record<string, [number, number, number]> = {
    black: [0, 0, 0],
    white: [255, 255, 255],
    red: [255, 0, 0],
    green: [0, 128, 0],
    blue: [0, 0, 255],
    yellow: [255, 255, 0],
    gray: [128, 128, 128],
  };
  const rgb = colors[color.toLowerCase()];
  return rgb
    ? PdfColor.fromRgb(rgb[0], rgb[1], rgb[2], opacity)
    : PdfColor.fromRgb(0, 0, 0, opacity);
}

function transformPathData(
  d: string,
  matrix: Matrix2D,
  scaleX: number,
  scaleY: number,
  offsetX: number,
  offsetY: number
): string {
  const tokens = d.match(/[a-zA-Z]|[-+]?\d*\.?\d+(?:[eE][-+]?\d+)?/g) || [];
  let result = '',
    cmd = '',
    i = 0;
  while (i < tokens.length) {
    if (/[a-zA-Z]/.test(tokens[i])) {
      cmd = tokens[i];
      result += `${cmd  } `;
      i++;
    }
    const isRel = cmd === cmd.toLowerCase(),
      upper = cmd.toUpperCase();
    const transformX = (x: number, y: number, rel: boolean) => {
      if (rel) return (matrix.a * x + matrix.c * y) * scaleX;
      return matrix.transformPoint(x, y).x * scaleX + offsetX;
    };
    const transformY = (x: number, y: number, rel: boolean) => {
      if (rel) return (matrix.b * x + matrix.d * y) * scaleY;
      return matrix.transformPoint(x, y).y * scaleY + offsetY;
    };

    const nextPoints = () => {
      const pts = [];
      while (i < tokens.length && !/[a-zA-Z]/.test(tokens[i])) pts.push(parseFloat(tokens[i++]));
      return pts;
    };

    const pts = nextPoints();
    let j = 0;
    while (j < pts.length) {
      if (upper === 'M' || upper === 'L' || upper === 'T') {
        result += `${transformX(pts[j], pts[j + 1], isRel)} ${transformY(pts[j], pts[j + 1], isRel)} `;
        j += 2;
      } else if (upper === 'H') {
        result += `${transformX(pts[j++], 0, isRel)} `;
      } else if (upper === 'V') {
        result += `${transformY(0, pts[j++], isRel)} `;
      } else if (upper === 'C') {
        result += `${transformX(pts[j], pts[j + 1], isRel)} ${transformY(pts[j], pts[j + 1], isRel)} ${transformX(pts[j + 2], pts[j + 3], isRel)} ${transformY(pts[j + 2], pts[j + 3], isRel)} ${transformX(pts[j + 4], pts[j + 5], isRel)} ${transformY(pts[j + 4], pts[j + 5], isRel)} `;
        j += 6;
      } else if (upper === 'S' || upper === 'Q') {
        result += `${transformX(pts[j], pts[j + 1], isRel)} ${transformY(pts[j], pts[j + 1], isRel)} ${transformX(pts[j + 2], pts[j + 3], isRel)} ${transformY(pts[j + 2], pts[j + 3], isRel)} `;
        j += 4;
      } else if (upper === 'A') {
        const rx = pts[j++] * matrix.a * scaleX,
          ry = pts[j++] * matrix.d * scaleY;
        const rot = pts[j++],
          large = pts[j++],
          sweep = pts[j++],
          tx = transformX(pts[j], pts[j + 1], isRel),
          ty = transformY(pts[j], pts[j + 1], isRel);
        j += 2;
        result += `${rx} ${ry} ${rot} ${large} ${sweep} ${tx} ${ty} `;
      } else j++;
    }
  }
  return result;
}
